/**
 * Effective Participant Resolution (CYCLE → TRACK → SESSION, users + groups)
 *
 * Participation is first-class at each scope with both people and groups:
 * - Cycle: cycle_participants, cycle_participant_groups
 * - Track: track_participants, track_participant_groups
 * - Session: session_participants, session_participant_groups (and participant_ids)
 *
 * Inheritance: additive-only (lower scopes add to effective set; no removal in first pass).
 * One row per effective user; provenance records all contributing scopes/types.
 *
 * Facilitator: always an individual (user id) from the effective participant set; never a group.
 */

// ============================================================================
// Types
// ============================================================================

/** Source tags for scoped resolution (track and session) */
export type ParticipationSource =
  | 'workflow_direct'
  | 'workflow_group'
  | 'cycle_direct'
  | 'cycle_group'
  | 'track_direct'
  | 'track_group'
  | 'session_direct'
  | 'session_group';

/** Legacy cycle-only provenance (for backward compatibility) */
export type EffectiveParticipantProvenance = 'direct' | 'group' | 'both';

export interface EffectiveParticipant {
  userId: string;
  /** How this user is in the participation set (legacy: 'direct' | 'group' | 'both') */
  provenance: EffectiveParticipantProvenance;
  /** When provenance includes 'group', which group ids they come from (for display) */
  viaGroupIds: string[];
  /** Scoped provenance: all contributing sources (cycle_direct, cycle_group, track_direct, etc.) */
  sources?: ParticipationSource[];
}

export interface ParticipationSetInput {
  /** Direct user participants (from cycle_participants, active) */
  directParticipants: Array<{ userId: string }>;
  /** Group participants (from cycle_participant_groups, active) */
  participantGroups: Array<{ groupId: string }>;
  /** Resolve group id -> member user ids */
  getMemberUserIdsForGroup: (groupId: string) => Promise<string[]>;
}

/** Input for track-level effective resolution (cycle + track) */
export interface TrackParticipationInput {
  workflowDirectUsers?: Array<{ userId: string }>;
  workflowParticipantGroups?: Array<{ groupId: string }>;
  cycleDirectUsers: Array<{ userId: string }>;
  cycleParticipantGroups: Array<{ groupId: string }>;
  trackDirectUsers: Array<{ userId: string }>;
  trackParticipantGroups: Array<{ groupId: string }>;
  getMemberUserIdsForGroup: (groupId: string) => Promise<string[]>;
}

/** Input for session-level effective resolution (cycle + track + session) */
export interface SessionParticipationInput extends TrackParticipationInput {
  sessionDirectUserIds: string[];
  sessionParticipantGroups: Array<{ groupId: string }>;
}

// ============================================================================
// Helpers: merge provenance and sources
// ============================================================================

function mergeProvenance(prev: EffectiveParticipantProvenance, fromGroup: boolean): EffectiveParticipantProvenance {
  if (prev === 'both') return 'both';
  if (fromGroup && prev === 'direct') return 'both';
  if (fromGroup && prev === 'group') return 'group';
  if (!fromGroup && prev === 'group') return prev;
  return prev === 'direct' ? 'direct' : 'both';
}

function addSource(sources: Set<ParticipationSource>, tag: ParticipationSource): void {
  sources.add(tag);
}

// ============================================================================
// Resolver: cycle-only (legacy)
// ============================================================================

/**
 * Compute effective participation from direct users and participating groups (cycle scope only).
 * - Each user appears at most once.
 * - Provenance: 'direct' only, 'group' only (viaGroupIds set), or 'both'.
 */
export async function resolveEffectiveParticipants(
  input: ParticipationSetInput
): Promise<EffectiveParticipant[]> {
  const { directParticipants, participantGroups, getMemberUserIdsForGroup } = input;
  const directSet = new Set(directParticipants.map(p => p.userId));

  const userIdToViaGroupIds = new Map<string, string[]>();

  for (const g of participantGroups) {
    const memberIds = await getMemberUserIdsForGroup(g.groupId);
    for (const uid of memberIds) {
      if (!uid) continue;
      const existing = userIdToViaGroupIds.get(uid) ?? [];
      if (!existing.includes(g.groupId)) {
        userIdToViaGroupIds.set(uid, [...existing, g.groupId]);
      }
    }
  }

  const allUserIds = new Set<string>([...directSet, ...userIdToViaGroupIds.keys()]);
  const result: EffectiveParticipant[] = [];

  for (const userId of allUserIds) {
    const viaGroupIds = userIdToViaGroupIds.get(userId) ?? [];
    const fromDirect = directSet.has(userId);
    const fromGroup = viaGroupIds.length > 0;
    const provenance: EffectiveParticipantProvenance =
      fromDirect && fromGroup ? 'both' : fromDirect ? 'direct' : 'group';
    result.push({ userId, provenance, viaGroupIds });
  }

  return result;
}

/**
 * Synchronous version when group members are already loaded (cycle-only).
 */
export function resolveEffectiveParticipantsSync(
  directParticipants: Array<{ userId: string }>,
  participantGroups: Array<{ groupId: string }>,
  groupIdToMemberIds: Map<string, string[]>
): EffectiveParticipant[] {
  const directSet = new Set(directParticipants.map(p => p.userId));
  const userIdToViaGroupIds = new Map<string, string[]>();

  for (const g of participantGroups) {
    const memberIds = groupIdToMemberIds.get(g.groupId) ?? [];
    for (const uid of memberIds) {
      if (!uid) continue;
      const existing = userIdToViaGroupIds.get(uid) ?? [];
      if (!existing.includes(g.groupId)) {
        userIdToViaGroupIds.set(uid, [...existing, g.groupId]);
      }
    }
  }

  const allUserIds = new Set<string>([...directSet, ...userIdToViaGroupIds.keys()]);
  const result: EffectiveParticipant[] = [];

  for (const userId of allUserIds) {
    const viaGroupIds = userIdToViaGroupIds.get(userId) ?? [];
    const fromDirect = directSet.has(userId);
    const fromGroup = viaGroupIds.length > 0;
    const provenance: EffectiveParticipantProvenance =
      fromDirect && fromGroup ? 'both' : fromDirect ? 'direct' : 'group';
    result.push({ userId, provenance, viaGroupIds });
  }

  return result;
}

// ============================================================================
// Resolver: track-level (cycle + track)
// ============================================================================

/**
 * Compute effective participants for a track from cycle (users + groups) and track (users + groups).
 * Additive only: cycle + track contributions merged; one row per user; provenance records all sources.
 */
export async function resolveEffectiveParticipantsForTrack(
  input: TrackParticipationInput
): Promise<EffectiveParticipant[]> {
  const {
    workflowDirectUsers = [],
    workflowParticipantGroups = [],
    cycleDirectUsers,
    cycleParticipantGroups,
    trackDirectUsers,
    trackParticipantGroups,
    getMemberUserIdsForGroup,
  } = input;

  const userIdToProvenance = new Map<string, EffectiveParticipantProvenance>();
  const userIdToViaGroupIds = new Map<string, string[]>();
  const userIdToSources = new Map<string, Set<ParticipationSource>>();

  function addUser(
    userId: string,
    fromDirect: boolean,
    fromGroup: boolean,
    groupId: string | null,
    source: ParticipationSource
  ): void {
    if (!userId) return;
    const prev = userIdToProvenance.get(userId) ?? (fromDirect ? 'direct' : 'group');
    userIdToProvenance.set(userId, mergeProvenance(prev, fromGroup));
    if (groupId) {
      const via = userIdToViaGroupIds.get(userId) ?? [];
      if (!via.includes(groupId)) userIdToViaGroupIds.set(userId, [...via, groupId]);
    }
    let sources = userIdToSources.get(userId);
    if (!sources) {
      sources = new Set();
      userIdToSources.set(userId, sources);
    }
    addSource(sources, source);
  }

  for (const u of workflowDirectUsers) {
    addUser(u.userId, true, false, null, 'workflow_direct');
  }
  for (const g of workflowParticipantGroups) {
    const memberIds = await getMemberUserIdsForGroup(g.groupId);
    for (const uid of memberIds) {
      addUser(uid, false, true, g.groupId, 'workflow_group');
    }
  }
  for (const u of cycleDirectUsers) {
    addUser(u.userId, true, false, null, 'cycle_direct');
  }
  for (const g of cycleParticipantGroups) {
    const memberIds = await getMemberUserIdsForGroup(g.groupId);
    for (const uid of memberIds) {
      addUser(uid, false, true, g.groupId, 'cycle_group');
    }
  }
  for (const u of trackDirectUsers) {
    addUser(u.userId, true, false, null, 'track_direct');
  }
  for (const g of trackParticipantGroups) {
    const memberIds = await getMemberUserIdsForGroup(g.groupId);
    for (const uid of memberIds) {
      addUser(uid, false, true, g.groupId, 'track_group');
    }
  }

  const result: EffectiveParticipant[] = [];
  for (const userId of userIdToProvenance.keys()) {
    result.push({
      userId,
      provenance: userIdToProvenance.get(userId)!,
      viaGroupIds: userIdToViaGroupIds.get(userId) ?? [],
      sources: Array.from(userIdToSources.get(userId)!),
    });
  }
  return result;
}

// ============================================================================
// Resolver: session-level (cycle + track + session)
// ============================================================================

/**
 * Compute effective participants for a session from cycle, track, and session (users + groups).
 * Additive only; one row per user; provenance includes session_direct and session_group when applicable.
 */
export async function resolveEffectiveParticipantsForSession(
  input: SessionParticipationInput
): Promise<EffectiveParticipant[]> {
  const trackInput: TrackParticipationInput = {
    workflowDirectUsers: input.workflowDirectUsers,
    workflowParticipantGroups: input.workflowParticipantGroups,
    cycleDirectUsers: input.cycleDirectUsers,
    cycleParticipantGroups: input.cycleParticipantGroups,
    trackDirectUsers: input.trackDirectUsers,
    trackParticipantGroups: input.trackParticipantGroups,
    getMemberUserIdsForGroup: input.getMemberUserIdsForGroup,
  };
  const trackEffective = await resolveEffectiveParticipantsForTrack(trackInput);

  const userIdToProvenance = new Map<string, EffectiveParticipantProvenance>();
  const userIdToViaGroupIds = new Map<string, string[]>();
  const userIdToSources = new Map<string, Set<ParticipationSource>>();

  for (const p of trackEffective) {
    userIdToProvenance.set(p.userId, p.provenance);
    userIdToViaGroupIds.set(p.userId, [...(p.viaGroupIds ?? [])]);
    userIdToSources.set(p.userId, new Set(p.sources ?? []));
  }

  const sessionDirectSet = new Set(input.sessionDirectUserIds);
  for (const uid of sessionDirectSet) {
    if (!uid) continue;
    const prev = userIdToProvenance.get(uid);
    userIdToProvenance.set(uid, mergeProvenance(prev ?? 'direct', false));
    if (!userIdToSources.has(uid)) userIdToSources.set(uid, new Set());
    userIdToSources.get(uid)!.add('session_direct');
  }

  for (const g of input.sessionParticipantGroups) {
    const memberIds = await input.getMemberUserIdsForGroup(g.groupId);
    for (const uid of memberIds) {
      if (!uid) continue;
      const prev = userIdToProvenance.get(uid);
      userIdToProvenance.set(uid, mergeProvenance(prev ?? 'group', true));
      const via = userIdToViaGroupIds.get(uid) ?? [];
      if (!via.includes(g.groupId)) userIdToViaGroupIds.set(uid, [...via, g.groupId]);
      if (!userIdToSources.has(uid)) userIdToSources.set(uid, new Set());
      userIdToSources.get(uid)!.add('session_group');
    }
  }

  const result: EffectiveParticipant[] = [];
  for (const userId of userIdToProvenance.keys()) {
    result.push({
      userId,
      provenance: userIdToProvenance.get(userId)!,
      viaGroupIds: userIdToViaGroupIds.get(userId) ?? [],
      sources: Array.from(userIdToSources.get(userId)!),
    });
  }
  return result;
}
